/**
 * An aquarium's seafloor, and the ocean's layers by depth.
 *
 * The seafloor is geography: static data in `data/seafloor/`, built from GEBCO
 * by `scripts/data/gebco.ts`, never saved. A sanctuary finds its own through
 * its site (`seafloorOf`, `data/sites.ts`). The aquarium is a grid of 1 km columns, each with the
 * depth of its water, the height of its land and the share of it that is
 * water; the water column in each is cut into the layers below.
 */

/** Unit conversions (definitions): a whole in percent, and meters in a kilometer. */
const PERCENT = 100;
const M_PER_KM = 1000;

/** A layer of the ocean by depth (`DEPTH_ZONES`). */
export type DepthZone = 'sunlit' | 'twilight' | 'midnight' | 'abyss' | 'hadal';

/**
 * The ocean's layers, as NOAA names them (NOAA JetStream, "Layers of the
 * Ocean"; the top 200 m as the epipelagic in NOAA's Coastal and Marine
 * Ecological Classification Standard): definitions, not measurements. Light
 * enough for photosynthesis reaches the sunlit zone, faint light the twilight
 * zone, none below. Phase 1's light model measures where the light really
 * ends at each place; these are the names the field gives the layers.
 */
export const DEPTH_ZONES: readonly { zone: DepthZone; name: string; topM: number; bottomM: number }[] = [
  { zone: 'sunlit', name: 'Sunlit (epipelagic)', topM: 0, bottomM: 200 },
  { zone: 'twilight', name: 'Twilight (mesopelagic)', topM: 200, bottomM: 1000 },
  { zone: 'midnight', name: 'Midnight (bathypelagic)', topM: 1000, bottomM: 4000 },
  { zone: 'abyss', name: 'Abyss (abyssopelagic)', topM: 4000, bottomM: 6000 },
  { zone: 'hadal', name: 'Trenches (hadalpelagic)', topM: 6000, bottomM: Infinity },
];

/** The layer a depth, m below the surface, lies in. A boundary belongs to the layer below it. */
export function depthZone(depthM: number): DepthZone {
  for (const z of DEPTH_ZONES) if (depthM < z.bottomM) return z.zone;
  return 'hadal';
}

/** The seafloor of one aquarium: a square of `cells` by `cells` columns, `cellKm` on a side. */
export interface Seafloor {
  /** The square's center, degrees (WGS 84). */
  centerLatDeg: number;
  centerLonDeg: number;
  cells: number;
  cellKm: number;
  /** Where the depths come from, as the readout names it. */
  source: string;
  /** Row-major, row 0 the northern edge, column 0 the western: mean depth of the water, m, 0 where there is none. */
  waterDepthM: readonly number[];
  /** Mean height of the land above sea level, m, 0 where there is none. */
  landHeightM: readonly number[];
  /** The share of the column that is water, percent. */
  waterPercent: readonly number[];
}

/** A column's mean elevation, m, negative below sea level: what a relief map draws. */
export function elevationM(floor: Seafloor, index: number): number {
  const wet = (floor.waterPercent[index] ?? 0) / PERCENT;
  return -(floor.waterDepthM[index] ?? 0) * wet + (floor.landHeightM[index] ?? 0) * (1 - wet);
}

export interface SeafloorSummary {
  /** The deepest column's water, m. */
  deepestM: number;
  /** The highest column's land, m. */
  highestM: number;
  /** The share of the square that is land, percent of its area. */
  landPercent: number;
  /** The share of the square's seafloor lying in each layer, percent of the water's area. */
  floorPercent: Record<DepthZone, number>;
  /** The water in the square, km³. */
  waterKm3: number;
}

/** What a seafloor holds, read off its columns. */
export function summarize(floor: Seafloor): SeafloorSummary {
  const n = floor.cells * floor.cells;
  const cellKm2 = floor.cellKm * floor.cellKm;
  const byZone: Record<DepthZone, number> = { sunlit: 0, twilight: 0, midnight: 0, abyss: 0, hadal: 0 };
  let deepestM = 0;
  let highestM = 0;
  let wetKm2 = 0;
  let waterKm3 = 0;
  for (let k = 0; k < n; k++) {
    const share = (floor.waterPercent[k] ?? 0) / PERCENT;
    const depth = floor.waterDepthM[k] ?? 0;
    deepestM = Math.max(deepestM, depth);
    highestM = Math.max(highestM, floor.landHeightM[k] ?? 0);
    if (share === 0) continue;
    wetKm2 += share * cellKm2;
    waterKm3 += (share * cellKm2 * depth) / M_PER_KM;
    byZone[depthZone(depth)] += share * cellKm2;
  }
  const floorPercent = { ...byZone };
  for (const z of DEPTH_ZONES) floorPercent[z.zone] = wetKm2 > 0 ? (PERCENT * byZone[z.zone]) / wetKm2 : 0;
  return { deepestM, highestM, landPercent: PERCENT - (PERCENT * wetKm2) / (n * cellKm2), floorPercent, waterKm3 };
}
